import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ElementRef
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, of, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  switchMap,
  tap
} from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JobProcessingStateService } from '../../data/services/job-processing-state.service';
import { JobsService } from '../../data/services/jobs.service';
import { PresetCategoriesService } from '../../../preset-categories/data/services/preset-categories.service';
import { AiPresetsService } from '../../../ai-presets/data/services/ai-presets.service';
import { PresetCategoriesListResponse as CategoriesListResponse } from '../../../preset-categories/data/models/preset-categories.models';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import {
  PresetCategoryResponse,
  PresetResponse,
  ApiResponse,
  PresetsListResponse,
  PresetCategoriesListResponse,
  CompanyEnhancementSettings
} from '../../data/models/preset.models';
import { JobFormResponse } from '../../data/models/job-form.models';
import { JobPhotoItem } from '../../data/models/job-photo.models';

@Component({
  selector: 'app-job-enhancement',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './job-enhancement.html',
  styleUrl: './job-enhancement.scss'
})
export class JobEnhancement implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobsService = inject(JobsService);
  private readonly presetCategoriesService = inject(PresetCategoriesService);
  private readonly aiPresetsService = inject(AiPresetsService);
  private readonly jobProcessingStateService = inject(JobProcessingStateService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly categorySelection$ = new Subject<number>();

  readonly jobId = Number(this.route.snapshot.paramMap.get('id'));

  isLoading = false;
  isLoadingPresets = false;
  isSavingPreset = false;

  errorMessage = '';
  presetLoadError = '';
  savePresetError = '';
  testEnhancementError = '';

  cropImages = false;
  cropsPerImagePose = 4;

  presetCategories: PresetCategoryResponse[] = [];
  presets: PresetResponse[] = [];

  selectedPresetCategoryId: number | null = null;
  selectedPreset: PresetResponse | null = null;

  totalPresets = 0;

  @ViewChild('imageInput') imageInput!: ElementRef<HTMLInputElement>;

  selectedTestImage: JobPhotoItem | null = null;
  
  isTestingPreset = false;
  enhancementResultUrl: string | null = null;
  enhancementSettings: CompanyEnhancementSettings | null = null;
  sliderValue = 50;

  isZoomActive = false;
  isHoveringContainer = false;
  mouseX = 0;
  mouseY = 0;
  zoomLensRadius = 200;
  zoomFactor = 2.5;
  zoomImageWidth = 0;
  zoomImageHeight = 0;
  zoomImageX = 0;
  zoomImageY = 0;

  get currentOriginalImageUrl(): string | null {
    if (this.selectedTestImage) {
      return this.selectedTestImage.previewUrl;
    }
    if (this.enhancementSettings) {
      return this.enhancementSettings.originalImageUrl;
    }
    return null;
  }

  get currentEnhancedImageUrl(): string | null {
    if (this.enhancementResultUrl) {
      return this.enhancementResultUrl;
    }
    if (this.enhancementSettings && !this.selectedTestImage) {
      return this.enhancementSettings.enhancedImageUrl;
    }
    return null;
  }

  ngOnInit(): void {
    this.initializePresetCategorySelection();
    this.loadPageData();
  }

  private initializePresetCategorySelection(): void {
    this.categorySelection$
      .pipe(
        filter((presetCategoryId): presetCategoryId is number =>
          Number.isInteger(presetCategoryId) && presetCategoryId > 0
        ),
        distinctUntilChanged(),
        tap(() => {
          this.isLoadingPresets = true;
          this.presetLoadError = '';
          this.presets = [];
          this.totalPresets = 0;
          this.selectedPreset = null;
        }),
        switchMap(presetCategoryId =>
          this.jobsService.getPresetsByCategoryId(presetCategoryId).pipe(
            catchError((error: HttpErrorResponse) => {
              this.presetLoadError = this.getErrorMessage(
                error,
                'Failed to load presets for the selected category.'
              );

              return of(null);
            }),
            finalize(() => {
              this.isLoadingPresets = false;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response: ApiResponse<PresetsListResponse> | null) => {
        if (!response) {
          return;
        }

        if (!response.success || !response.data) {
          this.presetLoadError =
            response.message || 'Failed to load presets for the selected category.';
          return;
        }

        this.presets = response.data.presets ?? [];
        this.totalPresets = response.data.total ?? this.presets.length;

        let autoSelected = false;
        if (this.enhancementSettings && this.enhancementSettings.presetName) {
          const found = this.presets.find(p => p.name === this.enhancementSettings!.presetName);
          if (found) {
            this.selectedPreset = found;
            autoSelected = true;
          }
        }

        if (!autoSelected) {
          this.selectedPreset = this.presets.length > 0 ? this.presets[0] : null;
        }
      });
  }

  loadPageData(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const companyId = this.companyContextService.selectedCompanyId();

    forkJoin({
      job: this.jobsService.getJobById(this.jobId),
      presetCategoriesResponse: this.presetCategoriesService.getCategories(1, 1000, undefined, true).pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorMessage = this.getErrorMessage(
            error,
            'Failed to load preset categories.'
          );

          return of(null);
        })
      ),
      enhancementSettingsResponse: this.jobsService.getCompanyEnhancementSettings(companyId).pipe(
        catchError(() => of(null))
      ),
      allPresetsResponse: this.aiPresetsService.getPresets(0, 1000).pipe(
        catchError(() => of(null))
      )
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ job, presetCategoriesResponse, enhancementSettingsResponse, allPresetsResponse }: any) => {
          this.cropImages = job.cropImages;

          if (enhancementSettingsResponse && enhancementSettingsResponse.success && enhancementSettingsResponse.data) {
            this.enhancementSettings = enhancementSettingsResponse.data;
          }

          this.jobProcessingStateService.setJobFlags({
            jobId: this.jobId,
            isEnhancements: job.enhanceImages,
            isCropping: job.cropImages,
            isBackgroundExtraction: job.extractBackground,
            isBestPick: job.pickBestImage
          });

          const stateCount =
            this.jobProcessingStateService.getState(this.jobId).cropsPerImagePose;

          const apiCount = job.cropsPerImagePose;

          if (stateCount > 1 || (apiCount != null && apiCount > 0)) {
            this.cropsPerImagePose = stateCount > 1
              ? stateCount
              : Math.min(4, Math.max(1, apiCount!));
          }

          if (!presetCategoriesResponse?.success || !presetCategoriesResponse.data) {
            if (!this.errorMessage) {
              this.errorMessage =
                presetCategoriesResponse?.message || 'Failed to load preset categories.';
            }

            return;
          }

          this.presetCategories = (presetCategoriesResponse.data.categories ?? [])
            .filter((category: any) => category.isActive);

          if (this.presetCategories.length === 0) {
            this.errorMessage = 'No active preset categories are available.';
            return;
          }

          // Try to auto-select the category if we have enhancementSettings and all presets
          let defaultCategoryId = this.presetCategories[0].presetCategoryId;

          if (this.enhancementSettings && allPresetsResponse && allPresetsResponse.data && allPresetsResponse.data.presets) {
            const savedPresetName = this.enhancementSettings.presetName;
            const foundPreset = allPresetsResponse.data.presets.find((p: any) => p.name === savedPresetName);
            
            if (foundPreset && foundPreset.presetCategoryId) {
              const categoryExists = this.presetCategories.some(c => c.presetCategoryId === foundPreset.presetCategoryId);
              if (categoryExists) {
                defaultCategoryId = foundPreset.presetCategoryId;
              }
            }
          }

          this.selectedPresetCategoryId = defaultCategoryId;

          this.categorySelection$.next(this.selectedPresetCategoryId);
        },
        error: () => {
          this.errorMessage = 'Failed to load job enhancement details.';
        }
      });
  }

  toggleOriginalEnhanced(): void {
    if (this.sliderValue >= 50) {
      this.sliderValue = 0; // Show Enhanced
    } else {
      this.sliderValue = 100; // Show Original
    }
  }

  toggleZoom(): void {
    if (!this.currentEnhancedImageUrl) {
      return;
    }
    this.isZoomActive = !this.isZoomActive;
  }

  onContainerMouseEnter(): void {
    this.isHoveringContainer = true;
  }

  onContainerMouseLeave(): void {
    this.isHoveringContainer = false;
  }

  onContainerMouseMove(event: MouseEvent): void {
    if (!this.isZoomActive) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const rect = container.getBoundingClientRect();
    
    this.mouseX = event.clientX - rect.left;
    this.mouseY = event.clientY - rect.top;

    const containerWidth = rect.width;
    const containerHeight = rect.height;

    this.zoomImageWidth = containerWidth * this.zoomFactor;
    this.zoomImageHeight = containerHeight * this.zoomFactor;

    this.zoomImageX = this.zoomLensRadius - (this.mouseX * this.zoomFactor);
    this.zoomImageY = this.zoomLensRadius - (this.mouseY * this.zoomFactor);
  }

  onContainerContextMenu(event: MouseEvent): void {
    if (this.isZoomActive) {
      event.preventDefault();
      event.stopPropagation();
      this.isZoomActive = false;
    }
  }

  onPresetCategoryChanged(): void {
    const presetCategoryId = Number(this.selectedPresetCategoryId);

    if (!Number.isInteger(presetCategoryId) || presetCategoryId <= 0) {
      this.presets = [];
      this.totalPresets = 0;
      this.selectedPreset = null;
      return;
    }

    this.categorySelection$.next(presetCategoryId);
  }

  selectPreset(preset: PresetResponse): void {
    this.selectedPreset = preset;
  }

  onBackToJobs(): void {
    this.router.navigate(['/app/jobs']);
  }

  openImageSelector(): void {
    if (this.imageInput) {
      this.imageInput.nativeElement.value = '';
      this.imageInput.nativeElement.click();
    }
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    this.selectedTestImage = {
      id: `test-image-${Date.now()}`,
      clientFileId: crypto.randomUUID(),
      fileName: file.name,
      previewUrl: URL.createObjectURL(file),
      file: file,
      isSelected: false,
      uploadStatus: 'pending',
      uploadProgress: 0
    };
    
    this.enhancementResultUrl = null;
    this.testEnhancementError = '';
    this.sliderValue = 50;
  }

  onTestEnhancement(): void {
    if (!this.selectedTestImage || !this.selectedPreset || !this.selectedTestImage.file) {
      this.testEnhancementError = 'Please select a local test image with a file.';
      return;
    }
    
    this.isTestingPreset = true;
    this.testEnhancementError = '';
    const companyId = this.companyContextService.selectedCompanyId();
    
    this.jobsService.runEnhancementTest(companyId, this.selectedPreset.name, this.selectedTestImage.file)
      .pipe(
        finalize(() => {
          this.isTestingPreset = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.enhancementSettings = response.data;
            this.enhancementResultUrl = this.enhancementSettings.enhancedImageUrl;
          } else {
             this.testEnhancementError = response.message || 'Failed to run enhancement test.';
          }
        },
        error: (error: HttpErrorResponse) => {
          this.testEnhancementError = this.getErrorMessage(error, 'Failed to run enhancement test.');
        }
      });
  }

  onNext(): void {
    if (this.isSavingPreset || !this.selectedPreset) {
      return;
    }

    this.isSavingPreset = true;
    this.savePresetError = '';

    const companyId = this.companyContextService.selectedCompanyId();

    // Existing API is retained. It currently accepts Preset Name.
    // If the save API is upgraded later, send selectedPreset.presetId instead.
    this.jobsService
      .saveEnhancementPreset(
        this.jobId,
        this.selectedPreset.name,
        companyId
      )
      .pipe(
        finalize(() => {
          this.isSavingPreset = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: { success: boolean; message: string }) => {
          if (!response.success) {
            this.savePresetError =
              response.message || 'Failed to save enhancement preset.';
            return;
          }

          if (this.cropImages) {
            this.router.navigate(['/app/jobs', this.jobId, 'crops'], {
              queryParams: {
                cropsCount: this.cropsPerImagePose
              }
            });

            return;
          }

          this.router.navigate(['/app/jobs', this.jobId, 'photos']);
        },
        error: (error: HttpErrorResponse) => {
          this.savePresetError = this.getErrorMessage(
            error,
            'Failed to save enhancement preset.'
          );
        }
      });
  }

  trackByCategoryId(
    _: number,
    category: PresetCategoryResponse
  ): number {
    return category.presetCategoryId;
  }

  trackByPresetId(_: number, preset: PresetResponse): number {
    return preset.presetId;
  }

  private getErrorMessage(
    error: HttpErrorResponse,
    fallbackMessage: string
  ): string {
    if (typeof error.error === 'string' && error.error.trim()) {
      return error.error;
    }

    if (error.error?.message) {
      return error.error.message;
    }

    return fallbackMessage;
  }
}